/**
 * Accounts Receivable Report Page
 */

import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { ArrowLeft, Clock, Copy, HandCoins, Info, Mail, MapPin, Phone, Receipt, Search, Smartphone, User } from 'lucide-react'

import api from '@/lib/api'
import { formatCurrency, formatDate, cn, formatClientWithNoteForExport, debounce } from '@/lib/utils'
import { useTranslation } from '@/hooks/useTranslation'
import { useToast } from '@/components/ui/toaster'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ExportButtons } from '@/components/ui/export-buttons'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { InfoTooltip } from '@/components/ui/tooltip'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface ReceivableItem {
  bill_id: number
  bill_number: string | null
  client_id: number | null
  client_name: string
  client_note?: string | null
  bill_date: string
  bill_created_at: string | null
  remaining: number
  days_old: number
  aging_category: string
}

interface ReceivablesData {
  items: ReceivableItem[]
  aging_summary: {
    '0-30': number
    '31-60': number
    '61-90': number
    '91+': number
  }
  total_receivable: number
  count: number
}

interface ClientReceivableItem {
  bill_id: number
  bill_number: string | null
  client_name: string
  client_note?: string | null
  created_at: string | null
  invoice_date: string | null
  updated_at: string | null
  bill_total: number
  total_deposited: number
  remaining: number
  days_old: number
  status?: 'credit' | 'pending'
}

interface ClientReceivablesData {
  client: {
    id: number
    name: string
    identification: string
    phone: string | null
    celphone: string | null
    address: string | null
    email: string | null
  } | null
  items: ClientReceivableItem[]
  total: number
  total_deposited: number
  current_balance: number
  count: number
  pending_items: ClientReceivableItem[]
  pending_total: number
  pending_count: number
  total_with_pending: number
}

/** True when both ISO timestamps land on the same calendar day for the viewer. */
function isSameDay(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false
  const da = new Date(a)
  const db = new Date(b)
  if (isNaN(da.getTime()) || isNaN(db.getTime())) return false
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  )
}

export function AccountsReceivableReport() {
  const { t } = useTranslation()
  const { toast } = useToast()
  const navigate = useNavigate()
  const [agingFilter, setAgingFilter] = useState<string>('all')
  const [searchInput, setSearchInput] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [view, setView] = useState<'aging' | 'by-client'>('aging')
  const [selectedClientId, setSelectedClientId] = useState<string>('')

  // Debounced search (client name + bill alias/note); filtering happens on the API.
  const debouncedSearch = useCallback(
    debounce((value: string) => setSearchQuery(value), 300),
    []
  )

  const handleSearchChange = (value: string) => {
    setSearchInput(value)
    debouncedSearch(value)
  }

  const { data: receivables, isLoading } = useQuery({
    queryKey: ['reports-receivables', agingFilter, searchQuery],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (agingFilter && agingFilter !== 'all') params.set('aging_range', agingFilter)
      if (searchQuery.trim()) params.set('search', searchQuery.trim())
      const qs = params.toString()
      const response = await api.get(
        `/reports/accounts-receivable/detailed${qs ? `?${qs}` : ''}`
      )
      return response.data as ReceivablesData
    },
    enabled: view === 'aging',
    placeholderData: keepPreviousData,
  })

  const { data: allReceivables } = useQuery({
    queryKey: ['reports-receivables-all-for-clients'],
    queryFn: async () => {
      // include_pending=true so clients that only have unfinished bills are selectable.
      const response = await api.get('/reports/accounts-receivable/detailed?include_pending=true')
      return response.data as ReceivablesData
    },
    enabled: view === 'by-client',
  })

  const clientOptions = useMemo(() => {
    const items = allReceivables?.items ?? []
    const seen = new Map<number, string>()
    for (const it of items) {
      if (it.client_id != null && !seen.has(it.client_id)) {
        seen.set(it.client_id, it.client_name)
      }
    }
    return Array.from(seen.entries())
      .map(([id, name]) => ({ value: String(id), label: name }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [allReceivables])

  const { data: clientReceivables, isLoading: isLoadingClient } = useQuery({
    // keepPreviousData → the client card + totals stay on screen while the
    // filtered refetch is in flight (no full-view flicker).
    queryKey: ['reports-receivables-by-client', selectedClientId, searchQuery],
    queryFn: async () => {
      const qs = searchQuery.trim() ? `?search=${encodeURIComponent(searchQuery.trim())}` : ''
      const response = await api.get(
        `/reports/accounts-receivable/by-client/${selectedClientId}${qs}`
      )
      return response.data as ClientReceivablesData
    },
    enabled: view === 'by-client' && !!selectedClientId,
    placeholderData: keepPreviousData,
  })

  // Combined list: credit receivables + unfinished (pending) bills, each tagged with status.
  const combinedClientItems = useMemo<ClientReceivableItem[]>(() => {
    if (!clientReceivables) return []
    const credit = (clientReceivables.items || []).map((it) => ({ ...it, status: 'credit' as const }))
    const pending = (clientReceivables.pending_items || []).map((it) => ({ ...it, status: 'pending' as const }))
    return [...credit, ...pending]
  }, [clientReceivables])

  // Las dos fechas se confunden con facilidad, sobre todo en las facturas dejadas
  // en "continuar después": el icono explica cuál es cuál.
  const datesHelp = (
    <InfoTooltip
      label={t('reports.aboutTheseDates')}
      content={
        <span className="block space-y-1">
          <span className="block">{t('reports.billCreatedDateHelp')}</span>
          <span className="block">{t('reports.billInvoiceDateHelp')}</span>
          <span className="block text-muted-foreground">{t('reports.billDatesDifferNote')}</span>
        </span>
      }
    >
      <Info className="h-3.5 w-3.5" />
    </InfoTooltip>
  )

  const copyForWhatsapp = () => {
    if (!clientReceivables?.client) return
    const credit = clientReceivables.items || []
    const pending = clientReceivables.pending_items || []
    // Al cliente se le manda la fecha del comprobante, que es la que tiene en mano.
    const line = (it: ClientReceivableItem) => {
      const date = it.invoice_date ?? it.created_at
      return `• ${it.bill_number || `#${it.bill_id}`} · ${date ? formatDate(date) : '-'} · ${formatCurrency(it.remaining)}`
    }

    const parts: string[] = [`*${t('reports.ar.statementOf', { name: clientReceivables.client.name })}*`, '']
    if (credit.length > 0) {
      parts.push(`*${t('reports.ar.creditReceivable')}:*`, ...credit.map(line), '')
    }
    if (pending.length > 0) {
      parts.push(`*${t('reports.ar.pendingNotFinalized')}:*`, ...pending.map(line), '')
    }
    parts.push(
      '——————',
      `*${t('reports.totalReceivable')}:* ${formatCurrency(clientReceivables.current_balance)}`,
      `*${t('reports.totalWithPending')}:* ${formatCurrency(clientReceivables.total_with_pending)}`,
    )
    navigator.clipboard.writeText(parts.join('\n'))
    toast({ title: t('common.copied') })
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10 shrink-0"
          onClick={() => navigate('/reports')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl md:text-2xl font-bold">{t('reports.accountsReceivable')}</h1>
          <p className="text-sm text-muted-foreground">
            {view === 'by-client'
              ? 'Reporte de Cuentas por Cobrar por Cliente'
              : 'Antigüedad de cuentas por cobrar'}
          </p>
        </div>
        {view === 'aging' ? (
          <ExportButtons
            data={(receivables?.items || []).map((it) => ({
              ...it,
              client_name: formatClientWithNoteForExport(it.client_name, it.client_note),
            }))}
            filename="cuentas-por-cobrar"
            columns={[
              { key: 'client_name', header: t('clients.name') },
              { key: 'bill_number', header: t('reports.ar.billNumber') },
              { key: 'bill_created_at', header: t('reports.billCreatedDate'), format: (v) => v ? formatDate(v as string) : '' },
              { key: 'bill_date', header: t('reports.billInvoiceDate'), format: (v) => v ? formatDate(v as string) : '' },
              { key: 'remaining', header: t('reports.ar.balance'), format: (v) => formatCurrency((v as number) || 0) },
              { key: 'days_old', header: t('reports.daysOld'), format: (v) => `${v || 0}` },
              { key: 'aging_category', header: t('reports.ar.aging') },
            ]}
          />
        ) : (
          <ExportButtons
            data={(clientReceivables?.items || []).map((it) => ({
              ...it,
              client_name: formatClientWithNoteForExport(it.client_name, it.client_note),
            }))}
            filename={
              clientReceivables?.client
                ? `cxc-${clientReceivables.client.name.replace(/\s+/g, '-').toLowerCase()}`
                : 'cuentas-por-cobrar-cliente'
            }
            columns={[
              { key: 'bill_number', header: t('reports.ar.billCode') },
              { key: 'client_name', header: t('reports.ar.clientName') },
              { key: 'created_at', header: t('reports.billCreatedDate'), format: (v) => v ? formatDate(v as string) : '' },
              { key: 'invoice_date', header: t('reports.billInvoiceDate'), format: (v) => v ? formatDate(v as string) : '' },
              { key: 'updated_at', header: t('reports.billModifiedDate'), format: (v) => v ? formatDate(v as string) : '' },
              { key: 'bill_total', header: t('reports.ar.totalAmount'), format: (v) => formatCurrency((v as number) || 0) },
              { key: 'total_deposited', header: t('reports.totalDeposited'), format: (v) => formatCurrency((v as number) || 0) },
              { key: 'remaining', header: t('reports.ar.totalDebits'), format: (v) => formatCurrency((v as number) || 0) },
            ]}
          />
        )}
      </div>

      <Tabs value={view} onValueChange={(v) => setView(v as 'aging' | 'by-client')}>
        <TabsList className="grid w-full grid-cols-2 md:w-auto md:inline-flex">
          <TabsTrigger value="aging">{t('reports.ar.byAging')}</TabsTrigger>
          <TabsTrigger value="by-client">{t('reports.ar.byClient')}</TabsTrigger>
        </TabsList>

        <TabsContent value="aging" className="space-y-4 md:space-y-6 mt-4">
          {/* Aging Summary */}
          {receivables?.aging_summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card className="bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800">
                <CardContent className="p-3 text-center">
                  <p className="text-xs text-muted-foreground">0-30 {t('reports.days')}</p>
                  <p className="text-lg font-bold text-green-600">{formatCurrency(receivables.aging_summary['0-30'])}</p>
                </CardContent>
              </Card>
              <Card className="bg-yellow-50 dark:bg-yellow-950 border-yellow-200 dark:border-yellow-800">
                <CardContent className="p-3 text-center">
                  <p className="text-xs text-muted-foreground">31-60 {t('reports.days')}</p>
                  <p className="text-lg font-bold text-yellow-600">{formatCurrency(receivables.aging_summary['31-60'])}</p>
                </CardContent>
              </Card>
              <Card className="bg-orange-50 dark:bg-orange-950 border-orange-200 dark:border-orange-800">
                <CardContent className="p-3 text-center">
                  <p className="text-xs text-muted-foreground">61-90 {t('reports.days')}</p>
                  <p className="text-lg font-bold text-orange-600">{formatCurrency(receivables.aging_summary['61-90'])}</p>
                </CardContent>
              </Card>
              <Card className="bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800">
                <CardContent className="p-3 text-center">
                  <p className="text-xs text-muted-foreground">91+ {t('reports.days')}</p>
                  <p className="text-lg font-bold text-red-600">{formatCurrency(receivables.aging_summary['91+'])}</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={t('reports.searchClientOrAlias')}
              className="w-full sm:flex-1 md:max-w-[320px]"
            />
            <Select value={agingFilter} onValueChange={setAgingFilter}>
              <SelectTrigger className="w-full sm:w-[250px]">
                <SelectValue placeholder={t('reports.allAging')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('reports.allAging')}</SelectItem>
                <SelectItem value="0-30">{t('reports.aging0to30')}</SelectItem>
                <SelectItem value="31-60">{t('reports.aging31to60')}</SelectItem>
                <SelectItem value="61-90">{t('reports.aging61to90')}</SelectItem>
                <SelectItem value="91+">{t('reports.aging91plus')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* List */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="h-4 w-4 text-red-500" />
                Facturas Pendientes ({receivables?.items?.length || 0})
                {receivables?.total_receivable !== undefined && (
                  <span className="ml-auto text-red-600 font-bold">
                    Total: {formatCurrency(receivables.total_receivable)}
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-3">
                  {[...Array(10)].map((_, i) => (
                    <Skeleton key={i} className="h-16" />
                  ))}
                </div>
              ) : receivables?.items && receivables.items.length > 0 ? (
                <div className="space-y-2">
                  {receivables.items.map((item) => (
                    <div
                      key={item.bill_id}
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/30 active:bg-muted cursor-pointer touch-manipulation"
                      onClick={() => item.client_id && navigate(`/clients/${item.client_id}`)}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium break-words">
                          {formatClientWithNoteForExport(item.client_name, item.client_note)}
                        </p>
                        <p className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-1">
                          <span>Factura: {item.bill_number || item.bill_id}</span>
                          {/* La fecha que manda es la del comprobante; la de creación
                              solo se muestra cuando no cayeron el mismo día. */}
                          {isSameDay(item.bill_created_at, item.bill_date) ? (
                            <span>- {formatDate(item.bill_date)}</span>
                          ) : (
                            <>
                              <span>
                                - {t('reports.billCreatedDate')} {item.bill_created_at ? formatDate(item.bill_created_at) : '-'}
                              </span>
                              <span>
                                · {t('reports.billInvoiceDate')} {formatDate(item.bill_date)}
                              </span>
                              {datesHelp}
                            </>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <div className="text-right">
                          <p className="font-bold text-red-500">
                            {formatCurrency(item.remaining)}
                          </p>
                          <p className={cn(
                            "text-xs font-medium",
                            item.days_old <= 30 ? "text-green-600" :
                            item.days_old <= 60 ? "text-yellow-600" :
                            item.days_old <= 90 ? "text-orange-600" : "text-red-600"
                          )}>
                            {item.days_old} {t('reports.days')}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">
                  {t('reports.noPendingAccounts')}
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="by-client" className="space-y-4 md:space-y-6 mt-4">
          {/* Client Selector */}
          <Card>
            <CardContent className="p-4 space-y-2">
              <label className="text-sm font-medium">{t('reports.ar.clientName')}</label>
              <SearchableSelect
                options={clientOptions}
                value={selectedClientId}
                onValueChange={setSelectedClientId}
                placeholder={t('reports.ar.selectClient')}
                searchPlaceholder={t('reports.ar.searchClient')}
                emptyMessage={t('reports.ar.noClients')}
                className="w-full md:w-[400px]"
              />
            </CardContent>
          </Card>

          {!selectedClientId ? (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                {t('reports.ar.selectClientHint')}
              </CardContent>
            </Card>
          ) : isLoadingClient ? (
            <div className="space-y-3">
              <Skeleton className="h-32" />
              <Skeleton className="h-64" />
            </div>
          ) : clientReceivables?.client ? (
            <>
              {/* Client Header */}
              <Card>
                <CardContent className="p-4 grid gap-2 md:grid-cols-2">
                  <div className="flex items-center gap-2 text-sm">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{clientReceivables.client.name}</span>
                    <span className="text-muted-foreground">
                      ({clientReceivables.client.identification})
                    </span>
                  </div>
                  {clientReceivables.client.phone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{t('reports.ar.phone')}:</span>
                      <span>{clientReceivables.client.phone}</span>
                    </div>
                  )}
                  {clientReceivables.client.celphone && (
                    <div className="flex items-center gap-2 text-sm">
                      <Smartphone className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{t('reports.ar.mobile')}:</span>
                      <span>{clientReceivables.client.celphone}</span>
                    </div>
                  )}
                  {clientReceivables.client.email && (
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{t('settings.email')}:</span>
                      <span className="truncate">{clientReceivables.client.email}</span>
                    </div>
                  )}
                  {clientReceivables.client.address && (
                    <div className="flex items-start gap-2 text-sm md:col-span-2">
                      <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                      <span className="font-medium">{t('reports.ar.address')}:</span>
                      <span>{clientReceivables.client.address}</span>
                    </div>
                  )}
                  <div className="md:col-span-2 flex justify-end pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        navigate(`/clients/${clientReceivables.client!.id}/deposit`)
                      }
                      className="gap-2"
                    >
                      <HandCoins className="h-4 w-4" />
                      {t('deposits.globalDeposit')}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Totals Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Card>
                  <CardContent className="p-3 text-center">
                    <p className="text-xs text-muted-foreground">{t('reports.total')}</p>
                    <p className="text-lg font-bold">
                      {formatCurrency(clientReceivables.total)}
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800">
                  <CardContent className="p-3 text-center">
                    <p className="text-xs text-muted-foreground">{t('reports.totalDeposited')}</p>
                    <p className="text-lg font-bold text-success">
                      {formatCurrency(clientReceivables.total_deposited)}
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800">
                  <CardContent className="p-3 text-center">
                    <p className="text-xs text-muted-foreground">{t('reports.ar.currentBalance')}</p>
                    <p className="text-lg font-bold text-destructive">
                      {formatCurrency(clientReceivables.current_balance)}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Receivable totals: without vs with unfinished (pending) bills */}
              {clientReceivables.pending_count > 0 && (
                <Card className="border-amber-200 dark:border-amber-900/40">
                  <CardContent className="p-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="p-3 rounded-lg bg-muted/40 text-center">
                        <p className="text-xs text-muted-foreground">{t('reports.totalReceivable')}</p>
                        <p className="text-xl font-bold text-red-600">
                          {formatCurrency(clientReceivables.current_balance)}
                        </p>
                      </div>
                      <div className="p-3 rounded-lg bg-amber-50/60 dark:bg-amber-950/20 text-center">
                        <p className="text-xs text-muted-foreground">{t('reports.totalWithPending')}</p>
                        <p className="text-xl font-bold text-amber-700 dark:text-amber-400">
                          {formatCurrency(clientReceivables.total_with_pending)}
                        </p>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2 text-center">
                      {t('reports.pendingNotInDebt')}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Bills Table (credit + unfinished pending, combined) */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <CardTitle className="text-base">
                      Facturas Pendientes ({combinedClientItems.length})
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                        <Input
                          value={searchInput}
                          onChange={(e) => handleSearchChange(e.target.value)}
                          placeholder={t('reports.searchByAlias')}
                          className="h-8 w-[170px] sm:w-[200px] pl-8 text-sm"
                        />
                      </div>
                      {combinedClientItems.length > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-2 shrink-0"
                          onClick={copyForWhatsapp}
                        >
                          <Copy className="h-4 w-4" />
                          <span className="hidden sm:inline">{t('reports.copyForWhatsapp')}</span>
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {combinedClientItems.length > 0 ? (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>{t('reports.ar.billCode')}</TableHead>
                            <TableHead>{t('common.status')}</TableHead>
                            <TableHead>{t('reports.ar.clientName')}</TableHead>
                            <TableHead>
                              <span className="inline-flex items-center gap-1">
                                {t('reports.billCreatedDate')}
                                {datesHelp}
                              </span>
                            </TableHead>
                            <TableHead>{t('reports.billInvoiceDate')}</TableHead>
                            <TableHead>{t('reports.billModifiedDate')}</TableHead>
                            <TableHead className="text-right">{t('reports.ar.totalAmount')}</TableHead>
                            <TableHead className="text-right">{t('reports.totalDeposited')}</TableHead>
                            <TableHead className="text-right">{t('reports.ar.totalDebits')}</TableHead>
                            <TableHead className="text-right w-[80px]">{t('common.actions')}</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {combinedClientItems.map((item) => {
                            const isPending = item.status === 'pending'
                            return (
                            <TableRow key={`${item.status}-${item.bill_id}`} className={cn(isPending && 'bg-amber-50/40 dark:bg-amber-950/10')}>
                              <TableCell className="font-medium">
                                {item.bill_number || item.bill_id}
                              </TableCell>
                              <TableCell>
                                <Badge variant={isPending ? 'outline' : 'secondary'} className={cn(isPending && 'border-amber-400 text-amber-700 dark:text-amber-400')}>
                                  {isPending ? t('reports.statusPending') : t('reports.statusCredit')}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                {formatClientWithNoteForExport(item.client_name, item.client_note)}
                              </TableCell>
                              <TableCell>
                                {item.created_at ? formatDate(item.created_at) : '-'}
                              </TableCell>
                              <TableCell>
                                {item.invoice_date ? formatDate(item.invoice_date) : '-'}
                              </TableCell>
                              <TableCell>
                                {item.updated_at ? formatDate(item.updated_at) : '-'}
                              </TableCell>
                              <TableCell className="text-right">
                                {formatCurrency(item.bill_total)}
                              </TableCell>
                              <TableCell className="text-right text-green-600">
                                {formatCurrency(item.total_deposited)}
                              </TableCell>
                              <TableCell className={cn('text-right font-bold', isPending ? 'text-amber-700 dark:text-amber-400' : 'text-red-600')}>
                                {formatCurrency(item.remaining)}
                              </TableCell>
                              <TableCell className="text-right">
                                {isPending ? (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    title={t('deposits.viewDetails')}
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      navigate(`/bills/${item.bill_id}`)
                                    }}
                                  >
                                    <Receipt className="h-4 w-4" />
                                  </Button>
                                ) : (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    title={t('deposits.newDeposit')}
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      navigate(`/bills/${item.bill_id}/deposit`)
                                    }}
                                  >
                                    <Receipt className="h-4 w-4" />
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                            )
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-center py-8">
                      {t('reports.noPendingAccounts')}
                    </p>
                  )}
                </CardContent>
              </Card>
            </>
          ) : (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                {t('reports.noPendingAccounts')}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}
